import './LogsTable.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MUIDataTable, {
  type MUIDataTableColumn,
  type MUIDataTableMeta,
  type MUIDataTableOptions,
  type MUIDataTableState,
} from 'mui-datatables';
import { useSocket } from 'src/utils/socket';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useHistory, useLocation } from 'react-router';
import globalStyles from '../../global-styles/rootGlobalStyles';
import { dateFormatter } from '../../utils/helpers/timeHelpers';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';
import { DqLoading } from '../DqLoading';
import { FormattedMessage, useIntl } from 'react-intl';
import { JobMessages } from '../../intl/JobMessages';
import { GenericMessages } from '../../intl/GenericMessages';
import { ControllerMessages } from '../../intl/ControllerMessages';
import SentinelLogo from '../../assets/images/SentinelLogo.png';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { Roles } from '@dataquiver/stoneage-roles';
import { LogUploadFormFields, LogUploadModal } from '../log-upload-modal/LogUploadModal';
import { createJobLogAndUpload } from '../../utils/api/uploadLogFileToS3';
import { IonButton, IonIcon } from '@ionic/react';
import { createNextState } from '@reduxjs/toolkit';
import { debounce } from 'lodash';
import { LogSearchResult } from '../../models/jobLog';
import { AppRouteConstants } from '../../pages/app-layout/AppRouteConstants';
import { useToast } from '../reusable-components/useToast';
import { filterOutExpiredEmailGuestShares, filterOutExpiredPermissions } from '../../utils/logs/logHelpers';
import { MobileLogListPageActions } from '../../pages/jobs/MobileLogsListPage';
import { pencil } from 'ionicons/icons';
import UpdateJobDetailsModal, {
  UpdateJobDetailsModalProps,
} from '../job-details/job-details-update/UpdateJobDetailsModal';
import { fetchLogTagsByOrgId } from '../../redux/logTags/thunk/logTags';
import { logTagsActions } from '../../redux/logTags/actions';
import { selectLogTags } from '../../redux/logTags/selector';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../redux/types';
import { AnyAction } from 'redux';
import { canUpdateJobLog } from '../../utils/permissions/logs';

export interface LogsSearchParams {
  page?: number;
  pageSize?: number;
  sort?: [string, 'asc' | 'desc'];
  orgIds?: string[];
  physicalSerials?: string[];
  deviceTimezones?: string[];
  searchTerm?: string;
  logTagIds?: string[];
}

/**
 * MUI DataTable and the API disagree on the field name to sort by organization name
 * @param apiSort
 */
function apiToMuiSort(apiSort?: [string, 'asc' | 'desc']) {
  if (!apiSort) return undefined;

  if (apiSort[0] === 'org.orgName') {
    return ['org', apiSort[1]];
  }

  return apiSort;
}

/**
 * MUI DataTable and the API disagree on the field name to sort by organization name
 * @param muiSort
 */
function muiToApiSort(muiSort: [string, 'asc' | 'desc']): [string, 'asc' | 'desc'] {
  if (muiSort[0] === 'org') {
    return ['org.orgName', muiSort[1]];
  }
  return muiSort;
}

function buildFilters(data: LogSearchResult, tableState: MUIDataTableState) {
  const filters: Record<string, string[]> = {};

  for (let i = 0; i < tableState.filterList.length; i++) {
    if (tableState.filterList[i].length > 0) {
      const columnName = tableState.columns[i].name;
      const values = tableState.filterList[i];

      if (columnName === 'org' && data.relations) {
        filters.orgIds = Object.values(data.relations.organizations)
          .filter(org => values.includes(org.orgName))
          .map(org => org._id);
      } else if (columnName === 'physicalSerial') {
        filters.physicalSerials = values;
      } else if (columnName === 'deviceTimezone') {
        filters.deviceTimezones = values;
      } else if (columnName === 'logTags' && data.relations) {
        filters.logTagIds = Object.values(data.relations.logTags)
          .filter(logTag => {
            return values.includes(logTag.tag);
          })
          .map(tag => tag._id);
      }
    }
  }
  return filters;
}

const LOG_ID_COLUMN_INDEX = 0;
const DESCRIPTION_COLUMN_INDEX = 1;
const DEVICE_TIMEZONE_COLUMN_INDEX = 6;
const TAG_COLUMN_INDEX = 11;
const PENCIL_COLUMN_INDEX = 12;
const ORIGINAL_FILENAME_COLUMN_INDEX = 13;
const LOG_TAG_ID_COLUMN_INDEX = 14;
const HEAT_EXCHANGER_MODEL_NUMBER_COLUMN_INDEX = 15;
const SITE_NAME_COLUMN_INDEX = 16;

const ROWS_PER_PAGE_OPTIONS = [10, 25, 40];

export default function LogsTable() {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const isMedium = useMediaQuery(`(max-width:${globalStyles.ScreenSize.Medium}`);
  const userData = useSelector(selectAuthUserInfo);
  const [query, setQuery] = useState<LogsSearchParams>({});
  const [columns, setColumns] = useState<MUIDataTableColumn[]>([]);
  const [data, setData] = useState<LogSearchResult>({
    results: [],
    totalCount: 0,
    relations: { organizations: {}, users: {}, physicalSerials: [], deviceTimezones: [], logTags: {} },
  });
  const [fetched, setFetched] = useState<boolean>(false);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
  const { request } = useSocket(SocketContextSentinel, 'logs');
  const [present] = useToast();
  const [updateJobDetailsModalProps, setUpdateJobDetailsModalProps] =
    useState<Partial<UpdateJobDetailsModalProps | undefined>>(undefined);
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();

  const fetchLogs = useCallback(() => {
    request<LogSearchResult>('logs search', query)
      .then(data => {
        setData(data);
        setFetched(true);
      })
      .catch(e => {
        console.error(e);
      });
  }, [query, request]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const user = useSelector(selectAuthUserInfo);
  useEffect(() => {
    if (user?.org) {
      dispatch(fetchLogTagsByOrgId(user.org));
    } else {
      dispatch(logTagsActions.tryFetchLogTagsFailure);
    }
  }, [dispatch, user?.org]);

  const tryFetchLogTagsState = useSelector(selectLogTags);
  const tags = useMemo(() => {
    return tryFetchLogTagsState.logTags?.map(logTag => logTag.tag) ?? [];
  }, [tryFetchLogTagsState]);

  // Sets up a history listener that detects when this page is pushed or popped.
  // When that happens the logs need to be re-fetched in case there has been a change
  // to a value due to UpdateJobDetailsModal, which is invoked from JobInfoByIdPage.
  // Must re-fetch on both push and pop because page is popped when navigating back
  // from JobInfoByIdPage using that page's back button, and page is pushed if user
  // navigates back by clicking Jobs -> Logs in the nav pane.
  // Note that this page gets pushed before the first render, but this will not be
  // detected because at that point this useEffect has not yet been invoked.
  useEffect(() => {
    const unregisterCallback = history.listen(location => {
      if (location.pathname === '/jobs/logs') {
        fetchLogs();
      }
    });

    return () => {
      unregisterCallback();
    };
    // We want this to be invoked once and that is it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClose = () => {
    setIsUploadModalOpen(false);
  };

  useEffect(() => {
    // CAUTION: We need to regenerate a lot of the options whenever data changes because it's captured
    // in the closure, and we need to make sure we use the latest. But we need to consider the previous
    // columns state, specifically which columns have been hidden. So we reinitialize most properties of
    // the columns but lookup the display property from the previous state and respect that.
    //
    // This prevents added columns from being removed when a new page of data loads in.
    setColumns(prev => [
      {
        label: formatMessage(JobMessages.logsTableIdColumn),
        name: '_id', // must be column 0! That way it is data[0] on rowClick. Thanks.
        options: {
          display: false,
          filter: false,
        },
      },
      {
        label: formatMessage(GenericMessages.descriptionLabel),
        name: 'description',
        options: {
          customBodyRender: (value: string | null, tableMeta: MUIDataTableMeta) => {
            if (value?.trim()) return value;
            const nameIndex = prev.findIndex(col => col.name === 'name');
            if (nameIndex === -1) return '???';
            return tableMeta.rowData?.[nameIndex]?.replace(/\.log$/i, '');
          },
          filter: false,
          display: prev.find(col => col.name === 'description')?.options?.display ?? true,
        },
      },
      {
        label: formatMessage(GenericMessages.nameLabel),
        name: 'name',
        options: {
          display: 'excluded',
          filter: false,
        },
      },
      {
        label: formatMessage(GenericMessages.orgLabel),
        name: 'org',
        options: {
          display: prev.find(col => col.name === 'org')?.options?.display ?? true,
          customBodyRender: (value: string) => {
            return data.relations?.organizations[value]?.orgName ?? formatMessage(GenericMessages.unknownPlaceholder);
          },
          filter: true,
          filterOptions: {
            names: data.relations?.organizations
              ? Object.values(data.relations.organizations).map(org => org.orgName)
              : [],
          },
        },
      },
      {
        label: formatMessage(ControllerMessages.controller),
        name: 'physicalSerial',
        options: {
          display: prev.find(col => col.name === 'physicalSerial')?.options?.display ?? !isMedium,
          filter: true,
          filterOptions: {
            names: data.relations?.physicalSerials ?? [],
          },
        },
      },
      {
        label: formatMessage(GenericMessages.uploadedBy),
        name: 'userEmail',
        options: {
          display: prev.find(col => col.name === 'userEmail')?.options?.display ?? !isMedium,
          filter: false,
          customBodyRenderLite: (dataIndex: number) => {
            const rowData = data.results[dataIndex];
            if (!rowData) return;

            // If log is user's org, resolve the user's email for display. If shared with user's org, resolve the name of the org
            // If either does not resolve, return 'Unknown'
            return (
              (rowData.org !== userData?.org
                ? data.relations?.organizations[rowData.org]?.orgName
                : data.relations?.users[rowData.createdBy]?.email) ?? formatMessage(GenericMessages.unknownPlaceholder)
            );
          },
        },
      },
      {
        label: formatMessage(JobMessages.logsTableDeviceTimezoneColumn),
        name: 'deviceTimezone',
        options: {
          display: !!prev.find(col => col.name === 'deviceTimezone')?.options?.display,
          filter: true,
          filterOptions: {
            names: data.relations?.deviceTimezones ?? [],
          },
        },
      },
      {
        label: formatMessage(JobMessages.logsTableUploadDateColumn),
        name: 'createdAt',
        options: {
          customBodyRender: (value: string | number | Date, tableMeta: { rowData: (string | undefined)[] }) => {
            const tzIndex = prev.findIndex(col => col.name === 'deviceTimezone');
            const tz = tzIndex >= 0 ? tableMeta?.rowData[tzIndex] : undefined;
            return dateFormatter(value, formatMessage(GenericMessages.unknownPlaceholder), tz);
          },
          filter: false,
          display: prev.find(col => col.name === 'createdAt')?.options?.display ?? false,
        },
      },
      {
        label: formatMessage(JobMessages.logsTableSizeColumn),
        name: 'linesParsed',
        options: {
          display: prev.find(col => col.name === 'linesParsed')?.options?.display ?? false,
          filter: false,
        },
      },
      {
        label: formatMessage(GenericMessages.statusLabel),
        name: 'status',
        options: {
          customBodyRender: (
            value: string | number | Date,
            tableMeta: { rowData: (string | undefined)[] },
            _updateValue: any,
          ) => {
            if (tableMeta?.rowData[0] === 'live') {
              return formatMessage(JobMessages.jobLive);
            }
            if (value === 'in-progress') {
              return formatMessage(JobMessages.jobInProgress);
            }
            return formatMessage(JobMessages.jobComplete);
          },
          display: prev.find(col => col.name === 'status')?.options?.display ?? true,
          filter: false,
        },
      },
      {
        label: formatMessage(JobMessages.logsTableSharedColumn),
        name: 'shared',
        options: {
          customBodyRenderLite: (dataIndex: number) => {
            const rowData = data.results[dataIndex];
            if (!rowData) return;
            const emailGuestShares = filterOutExpiredEmailGuestShares(rowData.emailGuestShares);
            const permissions = filterOutExpiredPermissions(rowData.permissions);
            const shared = permissions.length + emailGuestShares.length > 0;
            return formatMessage(shared ? GenericMessages.yesButtonLabel : GenericMessages.noButtonLabel);
          },
          display: prev.find(col => col.name === 'shared')?.options?.display ?? true,
          filter: false,
        },
      },
      {
        label: 'Tag',
        name: 'logTags',
        options: {
          customBodyRender: (
            value: { tag: string }[],
            _tableMeta: { rowData: (string | undefined)[] },
            _updateValue: any,
          ) => {
            if (!value || value.length < 1) return;
            return value[0].tag;
          },
          filterOptions: {
            names: tags,
          },
          customFilterListOptions: {
            render: value => {
              return value;
            },
          },
          display: prev.find(col => col.name === 'logTags')?.options?.display ?? true,
          filter: true,
        },
      },
      {
        label: '',
        name: '',
        options: {
          customBodyRender: (_value, tableMeta) => {
            return (
              <IonButton
                disabled={!canUpdateJobLog(userData, data.results[tableMeta.rowIndex])}
                className="job-summary-button"
                onClick={event => {
                  event.stopPropagation();
                  setUpdateJobDetailsModalProps({
                    jobId: tableMeta.rowData[LOG_ID_COLUMN_INDEX],
                    orgId: userData?.org,
                    originalFileName: tableMeta.rowData[ORIGINAL_FILENAME_COLUMN_INDEX],
                    tagId: tableMeta.rowData[LOG_TAG_ID_COLUMN_INDEX]?.[0],
                    existingJobDetailValues: {
                      description: tableMeta.rowData[DESCRIPTION_COLUMN_INDEX],
                      timezone: tableMeta.rowData[DEVICE_TIMEZONE_COLUMN_INDEX],
                      heatExchangerModelNumber: tableMeta.rowData[HEAT_EXCHANGER_MODEL_NUMBER_COLUMN_INDEX],
                      siteName: tableMeta.rowData[SITE_NAME_COLUMN_INDEX],
                    },
                  });
                }}
                fill="clear"
                size="small"
                color="dark"
              >
                <IonIcon slot="icon-only" icon={pencil} />
              </IonButton>
            );
          },
          display: user && !Roles.isUser(user.role, { isExact: true }),
          filter: false,
        },
      },
      {
        label: '',
        name: 'originalFileName',
        options: {
          display: false,
          filter: false,
          viewColumns: false,
        },
      },
      {
        label: '',
        name: 'logTagIds',
        options: {
          display: false,
          filter: false,
          viewColumns: false,
        },
      },
      {
        label: '',
        name: 'heatExchangerModelNumber',
        options: {
          display: false,
          filter: false,
          viewColumns: false,
        },
      },
      {
        label: '',
        name: 'siteName',
        options: {
          display: false,
          filter: false,
          viewColumns: false,
        },
      },
    ]);
  }, [data, formatMessage, isMedium, tags, user, userData]);

  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [orgFilters, setOrgFilters] = useState<string[]>([]);
  const [physicalSerialFilters, setPhysicalSerialFilters] = useState<string[]>([]);
  const [deviceTimeZoneFilters, setDeviceTimeZoneFilters] = useState<string[]>([]);
  const handleCellClick = (
    colData: string,
    cellMeta: {
      colIndex: number;
      rowIndex: number;
      dataIndex: number;
      event: React.MouseEvent;
    },
  ) => {
    if (cellMeta.colIndex === TAG_COLUMN_INDEX) {
      cellMeta.event.stopPropagation();
      const filteredCols = [...columns];
      if (filteredCols[TAG_COLUMN_INDEX].options && data.relations) {
        filteredCols[TAG_COLUMN_INDEX].options.filterList = [colData];
        const logTag = Object.values(data.relations.logTags).find(logTag => logTag.tag === colData)!;
        if (logTag) {
          setQuery(
            createNextState(draft => {
              draft.page = 0;
              draft.pageSize = rowsPerPage;
              draft.orgIds = orgFilters;
              draft.physicalSerials = physicalSerialFilters;
              draft.deviceTimezones = deviceTimeZoneFilters;
              draft.logTagIds = [logTag._id];
            }),
          );
          setColumns(filteredCols);
        }
      }
    }
    if (cellMeta.colIndex === PENCIL_COLUMN_INDEX) cellMeta.event.stopPropagation();
  };
  const handleView = (data: any[]) => {
    history.push(AppRouteConstants.LogDetails.replace(':id', data[0]));
  };

  const onLogUpload = async (formData: LogUploadFormFields, logFile: File) => {
    if (!formData || !logFile || !userData?.org) {
      setIsUploadModalOpen(false);
      return;
    }

    try {
      setIsUploadModalOpen(false);
      const blob = new Blob([logFile]);

      const fileContents = await blob.text();

      // Note: Using 'fromUI' for feature parity, this is a stopgap the old UI uses to upload a log file from dev page.
      const response = await createJobLogAndUpload(
        fileContents,
        userData.org,
        logFile.name,
        'fromUI',
        formData.description,
      );

      history.push(AppRouteConstants.LogDetails.replace(':id', response?.data?._id));
    } catch (err) {
      setIsUploadModalOpen(false);
      console.error('Error uploading job', err);
      present(
        {
          message: formatMessage(JobMessages.errorJobUpload),
        },
        'success',
      ).then(); // Suppresses "missing await" warning
    }
  };

  const { state: locationState } = useLocation<{ action?: MobileLogListPageActions; id?: undefined }>();
  useEffect(() => {
    // When a log file is deleted, the table needs to be refreshed so that the job will no longer appear.
    // Since the fetch is dependent on `query`, call `setQuery()` with a shallow copy of `query` so React
    // will consider the value to be changed.
    if (!!locationState?.action && locationState.action === MobileLogListPageActions.DeleteSuccessful) {
      setQuery({ ...query });
    }
  }, [locationState?.action, setQuery, query]);

  /**
   * Debounce a function for updating the search term query, we don't want
   * to fire off a request every key-press
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setSearchTerm = useCallback(
    debounce(
      (searchTerm: string | undefined) => {
        setQuery(
          createNextState(draft => {
            draft.searchTerm = searchTerm;
            draft.page = undefined;
          }),
        );
      },
      250,
      { maxWait: 1000 },
    ),
    [],
  );

  const handleTableChange = (eventType: string, tableState: MUIDataTableState) => {
    switch (eventType) {
      case 'viewColumnsChange':
        // It will only accept data like this, but it sends it back flattened
        const tempColumns = tableState.columns.map(column => ({ ...column, options: { ...column } }));
        setColumns(tempColumns);
        break;
      case 'sort':
        setQuery(
          createNextState(draft => {
            draft.sort = muiToApiSort([tableState.sortOrder.name, tableState.sortOrder.direction] as any);
            draft.page = 0;
            draft.pageSize = tableState.rowsPerPage;
          }),
        );
        break;
      case 'changePage':
        setQuery(
          createNextState(draft => {
            draft.page = tableState.page;
            draft.pageSize = tableState.rowsPerPage;
          }),
        );
        break;
      case 'changeRowsPerPage':
        setQuery(
          createNextState(draft => {
            draft.page = tableState.page;
            draft.pageSize = tableState.rowsPerPage;
          }),
        );
        setRowsPerPage(tableState.rowsPerPage);
        break;
      case 'filterChange':
      case 'resetFilters':
        const filters = buildFilters(data, tableState);
        setOrgFilters(filters.orgIds);
        setPhysicalSerialFilters(filters.physicalSerialFilters);
        setDeviceTimeZoneFilters(filters.deviceTimezones);
        setQuery(
          createNextState(draft => {
            draft.page = 0;
            draft.pageSize = tableState.rowsPerPage;
            draft.orgIds = filters.orgIds;
            draft.physicalSerials = filters.physicalSerials;
            draft.deviceTimezones = filters.deviceTimezones;
            draft.logTagIds = filters.logTagIds;
          }),
        );
        break;
      case 'search':
        setSearchTerm(tableState.searchText ?? undefined);
        break;
    }
  };
  const options: {} = {
    selectableRows: 'none',
    responsive: 'standard',
    onRowClick: handleView,
    onCellClick: handleCellClick,
    onTableChange: (eventType: string, tableState: MUIDataTableState) => handleTableChange(eventType, tableState),
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    download: false,
    print: false,
    serverSide: true,
    count: data.totalCount,
    sortOrder: apiToMuiSort(query.sort),
    customToolbar: Roles.isStoneAgeDev(userData?.role) // Upload log only available for StoneAge dev
      ? () => (
          <IonButton onClick={() => setIsUploadModalOpen(true)} color="primary">
            {formatMessage(JobMessages.uploadJobButtonLabel)}
          </IonButton>
        )
      : null,
    textLabels: {
      body: {
        noMatch: !fetched ? (
          <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <DqLoading width="100px" img={SentinelLogo} />
          </div>
        ) : (
          <FormattedMessage {...JobMessages.logsTableEmptyResults} />
        ),
        toolTip: formatMessage(GenericMessages.dataTablesSort),
      },
      pagination: {
        next: formatMessage(GenericMessages.dataTablesNextPage),
        previous: formatMessage(GenericMessages.dataTablesPreviousPage),
        rowsPerPage: formatMessage(GenericMessages.dataTablesRowsPerPage),
        displayRows: formatMessage(GenericMessages.of),
      },
      toolbar: {
        search: formatMessage(GenericMessages.searchPlaceholder),
        viewColumns: formatMessage(GenericMessages.dataTablesViewColumns),
        filterTable: formatMessage(GenericMessages.dataTablesFilterTable),
      },
      filter: {
        all: formatMessage(GenericMessages.dataTablesAll),
        title: formatMessage(GenericMessages.dataTablesFilters),
        reset: formatMessage(GenericMessages.dataTablesReset),
      },
      viewColumns: {
        title: formatMessage(GenericMessages.dataTablesShowColumns),
      },
    },
  } as MUIDataTableOptions;

  const onUpdateJobDetailsModalClose = useCallback(() => {
    setUpdateJobDetailsModalProps(undefined);
    fetchLogs();
  }, [fetchLogs]);

  return (
    <>
      <MUIDataTable columns={columns} data={data.results} options={options} title={undefined} />
      <LogUploadModal isOpen={isUploadModalOpen} onClose={() => onClose()} onSave={onLogUpload} />
      <UpdateJobDetailsModal
        jobId={updateJobDetailsModalProps?.jobId ?? ''}
        orgId={updateJobDetailsModalProps?.orgId ?? ''}
        originalFileName={updateJobDetailsModalProps?.originalFileName ?? ''}
        isOpen={!!updateJobDetailsModalProps}
        onClose={onUpdateJobDetailsModalClose}
        tagId={updateJobDetailsModalProps?.tagId ?? ''}
        existingJobDetailValues={{
          description: updateJobDetailsModalProps?.existingJobDetailValues?.description,
          heatExchangerModelNumber: updateJobDetailsModalProps?.existingJobDetailValues?.heatExchangerModelNumber,
          siteName: updateJobDetailsModalProps?.existingJobDetailValues?.siteName,
          timezone: updateJobDetailsModalProps?.existingJobDetailValues?.timezone,
        }}
      />
    </>
  );
}
